import * as React from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { useDeleteComplaintsCategory } from 'modules/complaints/hooks/useDeleteComplaintsCategory'
import { useUpdateComplaintsCategory } from 'modules/complaints/hooks/useUpdateComplaintsCategory'

import { CategorySettings } from './CategorySettings'
import { DeleteCategoryModal } from './DeleteCategoryModal'
import { EditCategoryModal } from './EditCategoryModal'
import type { CategoriesTableBodyProps, ComplaintsCategory } from './types'

const MODAL_OPEN = {
  NONE: 0,
  EDIT: 1,
  DELETE: 2,
} as const

type ModalOpen = (typeof MODAL_OPEN)[keyof typeof MODAL_OPEN]

export default function CategoriesTableBody({ categories }: CategoriesTableBodyProps) {
  const { deleteComplaintsCategory } = useDeleteComplaintsCategory()
  const { updateComplaintsCategory } = useUpdateComplaintsCategory()

  const [modalOpen, setModalOpen] = React.useState<ModalOpen>(MODAL_OPEN.NONE)
  const [selectedCategory, setSelectedCategory] = React.useState<ComplaintsCategory | null>(null)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleEdit = React.useCallback((category: ComplaintsCategory) => {
    setSelectedCategory(category)
    setModalOpen(MODAL_OPEN.EDIT)
  }, [])

  const handleToggleEnabled = React.useCallback(
    async (category: ComplaintsCategory) => {
      await updateComplaintsCategory({
        id: category.id,
        enabled: !category.enabled,
      })
    },
    [updateComplaintsCategory]
  )

  const handleDelete = React.useCallback((category: ComplaintsCategory) => {
    setSelectedCategory(category)
    setModalOpen(MODAL_OPEN.DELETE)
  }, [])

  const handleConfirmDelete = React.useCallback(async () => {
    if (!selectedCategory) return

    setIsDeleting(true)
    try {
      await deleteComplaintsCategory({
        id: selectedCategory.id,
      })
      setModalOpen(MODAL_OPEN.NONE)
      setSelectedCategory(null)
    } finally {
      setIsDeleting(false)
    }
  }, [selectedCategory, deleteComplaintsCategory])

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(MODAL_OPEN.NONE)
    setSelectedCategory(null)
  }, [])

  return (
    <>
      {categories.length === 0 ? (
        <Table.Row>
          <Table.Cell colSpan={12}>
            <Box width="full" alignItems="center" justifyContent="center">
              <Text.Normal>
                {i18n.t('app_store_v2.apps.complaints.settings.categories.no_categories')}
              </Text.Normal>
            </Box>
          </Table.Cell>
        </Table.Row>
      ) : (
        <>
          {categories.map((category) => (
            <Table.Row key={category.id}>
              <Table.Cell colSpan={6}>
                <Text.Normal ellipsis>{category.name}</Text.Normal>
              </Table.Cell>
              <Table.Cell colSpan={3}>
                {category.enabled
                  ? null
                  : i18n.t('app_store_v2.apps.complaints.settings.categories.table.hidden')}
              </Table.Cell>
              <Table.Cell colSpan={3}>
                <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
                  <CategorySettings
                    category={category}
                    onEdit={handleEdit}
                    onToggleEnabled={handleToggleEnabled}
                    onDelete={handleDelete}
                  />
                </Box>
              </Table.Cell>
            </Table.Row>
          ))}
        </>
      )}

      <DeleteCategoryModal
        category={modalOpen === MODAL_OPEN.DELETE ? selectedCategory : null}
        open={modalOpen === MODAL_OPEN.DELETE}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />

      <EditCategoryModal
        category={modalOpen === MODAL_OPEN.EDIT ? selectedCategory : null}
        open={modalOpen === MODAL_OPEN.EDIT}
        onClose={handleCloseModal}
      />
    </>
  )
}
